/**
 * Space Detail Page
 * Shows all queries within a specific space
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TablePagination,
  CircularProgress,
  Tooltip,
  Alert,
  Stack,
} from '@mui/material';
import { ConfirmDialog } from '../components/dialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import MenuBookRounded from '@mui/icons-material/MenuBookRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import { useNavigate, useParams } from 'react-router-dom';
import { formatRelativeTime } from '../utils/date';
import { getSpaceQueries } from '../services/spacesService';
import { deleteSavedQuery } from '../services/savedQueriesService';
import { useCopilotStore } from './copilot/stores/useCopilotStore';

interface SavedQuery {
  id: string;
  query_text: string;
  workflow_id: string;
  thread_id: string;
  agent_type: string;
  created_at: string;
  last_executed_at: string | null;
  execution_count: number;
  user_name: string;
  tags: string[];
}

export default function SpaceDetailPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { spaceId } = useParams<{ spaceId: string }>();
  
  const [spaceName, setSpaceName] = React.useState<string>('');
  const [spaceDescription, setSpaceDescription] = React.useState<string | null>(null);
  const [queries, setQueries] = React.useState<SavedQuery[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [total, setTotal] = React.useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [queryToDelete, setQueryToDelete] = React.useState<SavedQuery | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  // Fetch queries in this space
  const fetchQueries = React.useCallback(async () => {
    if (!spaceId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await getSpaceQueries(spaceId, page + 1, rowsPerPage);
      setQueries(data.queries);
      setTotal(data.total);
      setSpaceName(data.space.name);
      setSpaceDescription(data.space.description);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queries');
    } finally {
      setLoading(false);
    }
  }, [spaceId, page, rowsPerPage]);

  React.useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  const handleRun = (query: SavedQuery) => {
    if (query.agent_type === 'copilot') {
      // Reset the in-memory Copilot session before navigating so we don't
      // momentarily render the previously open thread's messages/artifacts
      // while the new thread's history is still being fetched.
      const store = useCopilotStore.getState();
      store.resetSession();
      store.setThreadId(query.thread_id);
      navigate(`/copilot/${query.thread_id}`);
    } else {
      navigate(`/chat?replay=${query.id}`);
    }
  };

  const handleDeleteClick = (query: SavedQuery) => {
    setQueryToDelete(query);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!queryToDelete) return;
    
    try {
      setDeleting(true);
      await deleteSavedQuery(queryToDelete.id);
      
      // Refresh the list
      await fetchQueries();
      setDeleteDialogOpen(false);
      setQueryToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete query');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      return formatRelativeTime(dateString);
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <Box>
      {/* Page Header - compact toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.25, sm: 2 },
          mb: 2,
          pb: 1.5,
          borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
          minHeight: { sm: 36 },
        }}
      >
        {isMobile && (
          <Button
            size="small"
            variant="text"
            startIcon={<ArrowBackRounded sx={{ fontSize: 20 }} />}
            onClick={() => navigate('/spaces')}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              px: 0.5,
              minWidth: 0,
              mb: 0.25,
            }}
            aria-label="Back to shared spaces"
          >
            Shared spaces
          </Button>
        )}
        <Box sx={{ minWidth: 0 }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              Loading space…
            </Typography>
          ) : (
            <>
              {spaceName ? (
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '1.05rem', sm: '1rem' }, lineHeight: 1.3, wordBreak: 'break-word', mb: 0.5 }}>
                  {spaceName}
                </Typography>
              ) : null}
              {spaceDescription ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.4, mb: 0.75 }}>
                  {spaceDescription}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                {total} {total === 1 ? 'query' : 'queries'} in this space
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Query list: cards on mobile, table on md+ */}
      {loading ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            py: 6,
            textAlign: 'center',
            borderRadius: 1,
          }}
        >
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontSize: '0.875rem' }}>
            Loading queries...
          </Typography>
        </Paper>
      ) : queries.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            py: 6,
            textAlign: 'center',
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '0.9375rem' }}>
            No queries in this space yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            Save queries from the chat to add them here
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.25}>
          {queries.map((query) => (
            <Paper
              key={query.id}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ display: 'flex', flexShrink: 0, pt: 0.25 }}>
                  {query.agent_type === 'copilot' ? (
                    <SmartToyOutlined sx={{ fontSize: 18, color: 'primary.main' }} />
                  ) : (
                    <BarChartRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8125rem',
                        lineHeight: 1.45,
                        wordBreak: 'break-word',
                        flex: 1,
                        minWidth: 0,
                        pr: 0.5,
                      }}
                    >
                      {query.query_text}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.25,
                        flexShrink: 0,
                        alignSelf: 'flex-start',
                        ml: 0.5,
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ChatBubbleOutlineRounded sx={{ fontSize: '16px !important' }} />}
                        onClick={() => handleRun(query)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          height: 28,
                          px: 1.25,
                          boxShadow: 'none',
                          '&:hover': { boxShadow: 'none' },
                        }}
                        aria-label="Open chat"
                      >
                        Open Chat
                      </Button>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(query)}
                          sx={{
                            p: 0.55,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.25,
                            color: 'error.main',
                            '&:hover': { borderColor: 'error.main', bgcolor: 'error.50' },
                          }}
                          aria-label="Delete query"
                        >
                          <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <PersonOutlineRounded sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {query.user_name}
                      </Typography>
                      <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.75rem', px: 0.25 }}>·</Box>
                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                        {query.execution_count} {query.execution_count === 1 ? 'run' : 'runs'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                      Created {formatDate(query.created_at)}
                      {' '}
                      <Box component="span" sx={{ color: 'text.disabled', mx: 0.5 }}>·</Box>
                      {' '}
                      Last run {formatDate(query.last_executed_at)}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
        >
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '36%', py: 1.25, fontSize: '0.8125rem' }}>Query</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '14%', py: 1.25, fontSize: '0.8125rem' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '13%', py: 1.25, fontSize: '0.8125rem' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '11%', py: 1.25, fontSize: '0.8125rem' }}>Last Run</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '7%', textAlign: 'center', py: 1.25, fontSize: '0.8125rem' }}>Total Runs</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '19%', textAlign: 'right', py: 1.25, fontSize: '0.8125rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queries.map((query) => (
                <TableRow key={query.id} hover>
                  <TableCell sx={{ py: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Tooltip title={query.agent_type === 'copilot' ? 'Copilot chat' : 'Saved query'} placement="top">
                        <Box sx={{ display: 'flex', flexShrink: 0 }}>
                          {query.agent_type === 'copilot'
                            ? <SmartToyOutlined sx={{ fontSize: 14, color: 'primary.main' }} />
                            : <BarChartRounded sx={{ fontSize: 14, color: 'text.disabled' }} />
                          }
                        </Box>
                      </Tooltip>
                      <Tooltip title={query.query_text} placement="top-start">
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 380,
                            fontSize: '0.8125rem',
                          }}
                        >
                          {query.query_text}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonOutlineRounded sx={{ fontSize: 15, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                        {query.user_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.25 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                      {formatDate(query.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.25 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                      {formatDate(query.last_executed_at)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', py: 1.25 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8125rem' }}>
                      {query.execution_count}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', py: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ChatBubbleOutlineRounded sx={{ fontSize: '16px !important' }} />}
                        onClick={() => handleRun(query)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          height: 30,
                          px: 1.25,
                          boxShadow: 'none',
                          whiteSpace: 'nowrap',
                          '&:hover': { boxShadow: 'none' },
                        }}
                      >
                        Open Chat
                      </Button>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(query)}
                          sx={{
                            p: 0.625,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            color: 'error.main',
                            '&:hover': { borderColor: 'error.main', bgcolor: 'error.50' },
                          }}
                        >
                          <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            px: { xs: 0.5, sm: 0 },
            '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1 },
          }}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        tone="destructive"
        title="Delete saved query?"
        confirmLabel="Delete"
        busy={deleting}
        description="This action cannot be undone."
        body={
          queryToDelete && (
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                {queryToDelete.query_text}
              </Typography>
            </Box>
          )
        }
      />
    </Box>
  );
}
